import { useState } from 'react';
import { Box, Tabs, Tab } from '@mui/material';
import { SideBySideViewProps } from './types';
import { DiscoveryPanel } from './DiscoveryPanel';
import { FeatureMatrixPanel } from './FeatureMatrixPanel';
import { OfficialQuotesPanel } from './OfficialQuotesPanel';
import { PricingPanel } from './PricingPanel';
import { RoiAnalysisPanel } from './RoiAnalysisPanel';
import { DataRoomPanel } from './DataRoomPanel';
import { MutualActionPlanPanel } from './MutualActionPlanPanel';
import {
  EngagementStepFooter,
  EngagementTab,
  EngagementTabKey,
  hasUnansweredRequired,
  LeaveStepDialog,
  visibleTabs
} from '../EngagementSteps';

export const SideBySideView = ({ steps, stepFlow }: SideBySideViewProps) => {
  const tabs = visibleTabs(steps);
  const [selectedKey, setSelectedKey] = useState<EngagementTabKey>('discovery');
  const [pendingKey, setPendingKey] = useState<EngagementTabKey | null>(null);
  const [stayRequests, setStayRequests] = useState(0);
  // Discovery is a required step, so it is always there to fall back to when the selected tab is turned off.
  const activeIndex = Math.max(
    tabs.findIndex((tab) => tab.key === selectedKey),
    0
  );
  const active = tabs[activeIndex];
  const activeKey = active.key;
  const progress = stepFlow?.requiredProgress?.[active.step ?? ''];
  const pending = tabs.find((tab) => tab.key === pendingKey);

  const stepName = (tab: EngagementTab) => steps?.find((step) => step.key === tab.step)?.label ?? tab.label;

  const goTo = (key: EngagementTabKey) => {
    const target = tabs.find((tab) => tab.key === key);
    if (target && target.step !== active.step && hasUnansweredRequired(progress)) {
      setPendingKey(key);
      return;
    }
    setSelectedKey(key);
  };

  const continueToPending = () => {
    if (pendingKey) setSelectedKey(pendingKey);
    setPendingKey(null);
  };

  return (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={activeKey}
          onChange={(_, key: EngagementTabKey) => goTo(key)}
          variant="scrollable"
          scrollButtons="auto">
          {tabs.map((tab) => (
            <Tab
              key={tab.key}
              value={tab.key}
              label={tab.label}
            />
          ))}
        </Tabs>
      </Box>

      <Box sx={{ minHeight: 400 }}>
        {activeKey === 'discovery' && <DiscoveryPanel focusIncomplete={stayRequests} />}

        {activeKey === 'features' && <FeatureMatrixPanel />}

        {activeKey === 'quotes' && <OfficialQuotesPanel />}

        {activeKey === 'pricing' && <PricingPanel />}

        {activeKey === 'roi' && <RoiAnalysisPanel />}

        {activeKey === 'data_room' && <DataRoomPanel />}

        {activeKey === 'action_plan' && <MutualActionPlanPanel />}
      </Box>

      {stepFlow && (
        <Box sx={{ borderTop: 1, borderColor: 'divider', mt: 3 }}>
          <EngagementStepFooter
            onBack={activeIndex > 0 ? () => setSelectedKey(tabs[activeIndex - 1].key) : stepFlow.onBack}
            onSaveAndQuit={stepFlow.onSaveAndQuit}
            saving={stepFlow.saving}
            onNext={activeIndex < tabs.length - 1 ? () => goTo(tabs[activeIndex + 1].key) : undefined}
          />
        </Box>
      )}

      {pending && hasUnansweredRequired(progress) && (
        <LeaveStepDialog
          open
          stepName={stepName(active)}
          destinationName={stepName(pending)}
          progress={progress}
          onStay={() => {
            setPendingKey(null);
            setStayRequests((count) => count + 1);
          }}
          onContinue={continueToPending}
        />
      )}
    </Box>
  );
};
